import { log } from '@shared/log.browser.ts';
import { TrackingService } from '../TrackingService.browser.ts';
import type {
    ResolvedSelectionResponse,
    SelectionDomAdapter,
    SelectionResolver,
    SelectionTrackingAdapter,
    TrackingIdentity,
} from './SelectionDependencies.ts';

const FILTER_RESOLVER_ENDPOINT = '/grasshopper/filter/resolve/dreson';
const TRACKING_CONTEXT_ID = 'reptile-sfid-wrapper';
const logger = log.scope('selection');
const INTEGER_PATTERN = /^(0|[1-9]\d*)$/;

export function createProductionSelectionDependencies(): {
    resolver: SelectionResolver;
    dom: SelectionDomAdapter;
    tracking: SelectionTrackingAdapter;
} {
    const trackingService = new TrackingService();
    return {
        resolver: resolveSelection,
        dom: browserSelectionDom,
        tracking: {
            replaceContext: (url) => trackingService.replaceContext(TRACKING_CONTEXT_ID, url),
        },
    };
}

export const browserSelectionDom: SelectionDomAdapter = {
    currentIdentity() {
        const rule = document.getElementById('avContent')?.dataset.rule;
        if (!rule) throw new Error('Failed to bootstrap selection: missing rule');
        /*                                                     */
        const tsBct = document.getElementById(TRACKING_CONTEXT_ID)?.querySelector<HTMLElement>('.ts-bct');
        const pageMergeId = tsBct?.dataset.ts_sfid ?? '';
        if (!pageMergeId) logger.error(`Missing pageMergeId in #${TRACKING_CONTEXT_ID}`);
        return { pageMergeId, rule };
    },
    applyIdentity({ pageMergeId, rule }: TrackingIdentity) {
        const wrapper = document.getElementById(TRACKING_CONTEXT_ID);
        const avContent = document.getElementById('avContent');
        if (!wrapper) throw new Error(`Failed to apply tracking identity: missing #${TRACKING_CONTEXT_ID}`);
        if (!avContent) throw new Error('Failed to apply tracking identity: missing #avContent');
        let tsBct = wrapper.querySelector<HTMLElement>('.ts-bct');
        if (!tsBct) {
            tsBct = document.createElement('div');
            tsBct.className = 'ts-bct';
            wrapper.prepend(tsBct);
        }
        tsBct.dataset.ts_sfid = pageMergeId;
        avContent.dataset.rule = rule;
    },
};

export async function resolveSelection(params: URLSearchParams): Promise<ResolvedSelectionResponse> {
    const response = await fetch(`${FILTER_RESOLVER_ENDPOINT}?${params.toString()}`, {
        headers: { 'X-Find-Trackable': 'ts_Assert=none' },
    });
    if (response.status !== 200) {
        throw new Error(`Failed to resolve rule: ${response.status} ${response.statusText}`);
    }
    return parseResolverResponse(await response.text());
}

export function parseResolverResponse(html: string): ResolvedSelectionResponse {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const element = parsed.querySelector<HTMLElement>('#avContent');
    const rule = element?.dataset.rule;
    const resolvedUrl = element?.dataset.redirect;
    if (!rule || !resolvedUrl) throw new Error('Failed to resolve rule: missing url or rule');

    const rawCount = element.dataset.count;
    let count: number | undefined;
    if (rawCount !== undefined && rawCount.length > 0) {
        if (!INTEGER_PATTERN.test(rawCount)) throw new Error('Failed to resolve rule: invalid count');
        count = Number(rawCount);
        if (!Number.isSafeInteger(count)) throw new Error('Failed to resolve rule: invalid count');
    }

    let url: URL;
    try {
        url = new URL(resolvedUrl, window.location.origin);
    } catch {
        throw new Error('Failed to resolve rule: invalid url');
    }
    if ((url.protocol !== 'http:' && url.protocol !== 'https:') || url.origin !== window.location.origin) {
        throw new Error('Failed to resolve rule: invalid url');
    }
    const pageMergeId = parsed.querySelector<HTMLElement>('.ts-bct')?.dataset.ts_sfid;
    if (!pageMergeId) throw new Error('Failed to resolve rule: missing pageMergeId');
    return {
        rule,
        url: url.href,
        ...(count !== undefined ? { count } : {}),
        pageMergeId,
    };
}
